import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/ui/BackButton';
import DeactivateUserConfirmModal from '../../components/ui/DeactivateUserConfirmModal';
import ReactivateUserConfirmModal from '../../components/ui/ReactivateUserConfirmModal';
import { workerService } from '../../services/workerService';
import { workerCertificationService } from '../../services/workerCertificationService';

import { companyService } from '../../services/companyService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

// jyyb8AfJ PR 2 — same 4-state taxonomy as workers-list. Duplicated here
// rather than imported because the workers-list module is a page (not a
// shared util) and the helper is one line; extracting to a utils file is
// reuse theatre for two callers.
const computeWorkerStatus = (worker) => {
  if (worker?.deactivated_at != null) return 'deactivated';
  if (worker?.is_active === true) return 'active';
  if (worker?.invited_by != null) return 'pending';
  return 'imported';
};


const WorkerDetail = () => {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [worker, setWorker] = useState(null);
  const [siteAssignments, setSiteAssignments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [showAddCertModal, setShowAddCertModal] = useState(false);
  const [company, setCompany] = useState(null);
  const [fileUrlCache, setFileUrlCache] = useState({});
  // jyyb8AfJ — admin actions
  const [actionModal, setActionModal] = useState(null); // 'deactivate' | 'reactivate' | null
  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    loadWorkerData();
  }, [workerId]);

  const loadWorkerData = async () => {
    try {
      setLoading(true);
      
      const [workerResult, sitesResult, activitiesResult, statsResult] = await Promise.all([
        workerService?.getWorkerById(workerId),
        // Use getWorkersBySite-compatible query: get sites for this worker
        // via both site_workers and worker_site_assignments
        workerService?.getWorkerSiteAssignments(workerId),
        workerService?.getWorkerActivities(workerId, 20),
        workerService?.getWorkerStats(workerId)
      ]);

      if (workerResult?.error) throw workerResult.error;
      
      setWorker(workerResult?.data);

      // Merge site assignments from both sources
      const siteAssignmentData = sitesResult?.data || [];

      // Also check worker_site_assignments for direct user assignments
      const { data: directAssignments } = await import('../../lib/supabase')?.then(({ supabase }) =>
        supabase
          ?.from('worker_site_assignments')
          ?.select(`
            site_id,
            assignment_date,
            sites (
              id,
              site_name,
              client_name,
              status
            )
          `)
          ?.eq('user_id', workerId)
      );

      // Combine both assignment sources, deduplicating by site_id
      const seenSiteIds = new Set(siteAssignmentData?.map(a => a?.site_id));
      const combined = [...siteAssignmentData];
      (directAssignments || [])?.forEach(da => {
        if (da?.sites && !seenSiteIds?.has(da?.site_id)) {
          seenSiteIds?.add(da?.site_id);
          combined?.push({
            site_id: da?.site_id,
            assigned_date: da?.assignment_date,
            is_active: true,
            sites: da?.sites
          });
        }
      });

      setSiteAssignments(combined);
      setActivities(activitiesResult?.data || []);
      setStats(statsResult?.data);

      // Load certifications
      loadCertifications();

      // Load company for add cert modal
      const companyResult = await companyService?.getUserCompany();
      if (!companyResult?.error) setCompany(companyResult?.data);
    } catch (err) {
      console.error('[WorkerDetail] load worker failed:', err);
      setError('Failed to load worker details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCertifications = async () => {
    try {
      setCertsLoading(true);
      const result = await workerCertificationService?.getCertificationsForWorker(workerId);
      if (!result?.error) setCertifications(result?.data || []);
    } catch (err) {
      console.error('Failed to load certifications:', err);
    } finally {
      setCertsLoading(false);
    }
  };

  const handleViewCertFile = async (cert) => {
    if (!cert?.file_path) return;
    if (fileUrlCache?.[cert?.id]) {
      window.open(fileUrlCache?.[cert?.id], '_blank');
      return;
    }
    const result = await workerCertificationService?.getCertificateFileUrl(cert?.file_path);
    if (result?.data) {
      setFileUrlCache(prev => ({ ...prev, [cert?.id]: result?.data }));
      window.open(result?.data, '_blank');
    }
  };

  const isManagerRole = userProfile?.role === 'admin' || userProfile?.role === 'site_manager';
  const isAdminViewer = userProfile?.role === 'admin';

  // jyyb8AfJ — show admin action when (a) viewer is admin, (b) target is
  // not self, (c) target is not the company's primary admin. Mirrors the
  // server-side guard order in set-user-deactivation; hiding the UI keeps
  // admins from the 403 motion. Reactivate skips the primary-admin check
  // because reactivating a never-deactivated primary admin is impossible.
  const workerStatus = computeWorkerStatus(worker);
  const isSelfTarget = worker?.id === userProfile?.id;
  const isPrimaryAdminTarget = worker?.id && company?.admin_user_id === worker?.id;
  const canShowDeactivate = isAdminViewer && workerStatus === 'active' && !isSelfTarget && !isPrimaryAdminTarget;
  const canShowReactivate = isAdminViewer && workerStatus === 'deactivated' && !isSelfTarget;

  const openDeactivate = () => { setActionError(''); setActionModal('deactivate'); };
  const openReactivate = () => { setActionError(''); setActionModal('reactivate'); };
  const closeActionModal = () => {
    if (actionInFlight) return;
    setActionModal(null);
    setActionError('');
  };
  const handleConfirmAction = async () => {
    if (!worker?.id) return;
    setActionInFlight(true);
    setActionError('');
    try {
      const result = actionModal === 'deactivate'
        ? await userService?.deactivateUser(worker.id)
        : await userService?.reactivateUser(worker.id);
      if (!result?.success) {
        setActionError(result?.error || 'Action failed');
        return;
      }
      setActionModal(null);
      await loadWorkerData();
    } finally {
      setActionInFlight(false);
    }
  };

  // jyyb8AfJ — 4-state badge (active / pending / deactivated / imported).
  // Two render slots: page-header (large) and Profile Information section
  // (compact). Both call this with a different `size` token.
  const renderStatusBadge = (size = 'large') => {
    const padClass = size === 'large' ? 'px-3 py-1.5 text-sm' : 'gap-1 text-sm';
    if (workerStatus === 'active') {
      return (
        <span className={`inline-flex items-center gap-1.5 ${padClass} rounded-full font-medium bg-success/10 text-success`}>
          <Icon name="CheckCircle2" size={size === 'large' ? 14 : 16} />
          Active
        </span>
      );
    }
    if (workerStatus === 'pending') {
      return (
        <span className={`inline-flex items-center gap-1.5 ${padClass} rounded-full font-medium bg-amber-100 text-amber-700`}>
          <Icon name="Clock" size={size === 'large' ? 14 : 16} />
          Pending
        </span>
      );
    }
    if (workerStatus === 'deactivated') {
      return (
        <span className={`inline-flex items-center gap-1.5 ${padClass} rounded-full font-medium bg-destructive/10 text-destructive`}>
          <Icon name="UserX" size={size === 'large' ? 14 : 16} />
          Deactivated
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1.5 ${padClass} rounded-full font-medium bg-muted text-muted-foreground`}>
        <Icon name="UserMinus" size={size === 'large' ? 14 : 16} />
        Imported
      </span>
    );
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-destructive/10 text-destructive';
      case 'site_manager':
        return 'bg-purple-100 text-purple-700';
      case 'worker':
        return 'bg-accent/10 text-accent';
      case 'client':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'document_uploaded':
        return 'FileText';
      case 'checklist_completed':
        return 'CheckSquare';
      case 'incident_reported':
        return 'AlertTriangle';
      case 'audit_completed':
        return 'ClipboardCheck';
      case 'worker_assigned':
        return 'UserPlus';
      case 'induction_completed':
        return 'GraduationCap';
      default:
        return 'Activity';
    }
  };

  const formatActivityType = (activityType) => {
    return activityType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  if (error || !worker) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">Worker Not Found</h3>
              <p className="text-muted-foreground mb-4">{error || 'The requested worker could not be found'}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/workers')}
                iconName="ArrowLeft"
              >
                Back to Workers
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <BackButton fallback="/workers" />
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground font-heading">{worker?.full_name || 'N/A'}</h1>
                <p className="text-muted-foreground mt-1">{worker?.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium capitalize ${getRoleBadgeColor(worker?.role)}`}>
                  {worker?.role || 'N/A'}
                </span>
                {renderStatusBadge('large')}
                {canShowDeactivate && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={openDeactivate}
                    iconName="UserX"
                    iconPosition="left"
                  >
                    Deactivate
                  </Button>
                )}
                {canShowReactivate && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={openReactivate}
                    iconName="UserCheck"
                    iconPosition="left"
                  >
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
            {actionError && (
              <div className="mt-3 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={18} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{actionError}</p>
                </div>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                  <Icon name="FileText" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground font-heading">{stats?.documentsUploaded || 0}</p>
                  <p className="text-xs text-muted-foreground font-caption">Documents</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                  <Icon name="CheckSquare" size={20} color="var(--color-success)" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground font-heading">{stats?.checklistsCompleted || 0}</p>
                  <p className="text-xs text-muted-foreground font-caption">Checklists</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-lg">
                  <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground font-heading">{stats?.incidentsLogged || 0}</p>
                  <p className="text-xs text-muted-foreground font-caption">Incidents</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg p-4 shadow-elevation-2 border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                  <Icon name="ClipboardCheck" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground font-heading">{stats?.auditsParticipated || 0}</p>
                  <p className="text-xs text-muted-foreground font-caption">Audits</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Profile Information */}
            <div className="bg-card rounded-lg p-6 shadow-elevation-2 border border-border">
              <h2 className="text-lg font-semibold text-foreground font-heading mb-4 flex items-center gap-2">
                <Icon name="User" size={20} color="var(--color-accent)" />
                Profile Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Full Name</p>
                  <p className="text-foreground font-medium">{worker?.full_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Email Address</p>
                  <p className="text-foreground font-medium">{worker?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Role</p>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(worker?.role)}`}>
                    {worker?.role || 'N/A'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Account Status</p>
                  {renderStatusBadge('compact')}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Joined Date</p>
                  <p className="text-foreground font-medium">
                    {worker?.created_at ? new Date(worker.created_at)?.toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    }) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-caption mb-1">Last Updated</p>
                  <p className="text-foreground font-medium">
                    {worker?.updated_at ? new Date(worker.updated_at)?.toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    }) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Site Assignments */}
            <div className="bg-card rounded-lg p-6 shadow-elevation-2 border border-border">
              <h2 className="text-lg font-semibold text-foreground font-heading mb-4 flex items-center gap-2">
                <Icon name="Building2" size={20} color="var(--color-accent)" />
                Assigned Sites ({siteAssignments?.length || 0})
              </h2>
              {siteAssignments?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Building2" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-muted-foreground">No site assignments</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {siteAssignments?.map((assignment) => (
                    <div
                      key={assignment?.site_id}
                      className="p-3 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                      onClick={() => navigate(`/sites/${assignment?.site_id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground mb-1">{assignment?.sites?.site_name || 'N/A'}</h4>
                          <p className="text-sm text-muted-foreground">{assignment?.sites?.client_name || 'N/A'}</p>
                        </div>
                        <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          Assigned: {assignment?.assigned_date ? new Date(assignment.assigned_date)?.toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-card rounded-lg p-6 shadow-elevation-2 border border-border mt-6">
            <h2 className="text-lg font-semibold text-foreground font-heading mb-4 flex items-center gap-2">
              <Icon name="Activity" size={20} color="var(--color-accent)" />
              Recent Activity
            </h2>
            {activities?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Activity" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-muted-foreground">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activities?.map((activity) => (
                  <div key={activity?.id} className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg flex-shrink-0">
                      <Icon name={getActivityIcon(activity?.activity_type)} size={20} color="var(--color-accent)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground mb-1">{activity?.title}</h4>
                      {activity?.description && (
                        <p className="text-sm text-muted-foreground mb-2">{activity?.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Tag" size={12} />
                          {formatActivityType(activity?.activity_type)}
                        </span>
                        {activity?.sites?.site_name && (
                          <span className="flex items-center gap-1">
                            <Icon name="Building2" size={12} />
                            {activity?.sites?.site_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          {new Date(activity?.created_at)?.toLocaleDateString('en-GB', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Certifications Section */}
          <div className="bg-card rounded-lg p-6 shadow-elevation-2 border border-border mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground font-heading flex items-center gap-2">
                <Icon name="Award" size={20} color="var(--color-accent)" />
                Certifications ({certifications?.length})
              </h2>
              {isManagerRole && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowAddCertModal(true)}
                >
                  Add
                </Button>
              )}
            </div>

            {certsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
              </div>
            ) : certifications?.length === 0 ? (
              <div className="text-center py-10">
                <Icon name="Award" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No certifications recorded</p>
                {isManagerRole && (
                  <p className="text-xs text-muted-foreground mt-1">Click Add to record a certification</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {certifications?.map(cert => {
                  const status = workerCertificationService?.getExpiryStatus(cert?.expiry_date);
                  return (
                    <div key={cert?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground text-sm">{cert?.certificate_type}</span>
                          {cert?.certificate_number && (
                            <span className="text-xs text-muted-foreground">#{cert?.certificate_number}</span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                            status === 'expired' ? 'bg-destructive/10 text-destructive' :
                            status === 'expiring_soon'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              status === 'expired' ? 'bg-destructive' :
                              status === 'expiring_soon' ? 'bg-warning' : 'bg-success'
                            }`} />
                            {status === 'expired' ? 'Expired' : status === 'expiring_soon' ? 'Expiring Soon' : 'Valid'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {workerCertificationService?.formatExpiryDate(cert?.expiry_date)}
                        </p>
                      </div>
                      {cert?.file_path && (
                        <button
                          onClick={() => handleViewCertFile(cert)}
                          className="flex items-center gap-1.5 text-xs text-accent hover:underline ml-3 flex-shrink-0"
                        >
                          <Icon name="FileText" size={13} />
                          View
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      <DeactivateUserConfirmModal
        isOpen={actionModal === 'deactivate'}
        onClose={closeActionModal}
        onConfirm={handleConfirmAction}
        userName={worker?.full_name || worker?.email}
        isDeactivating={actionInFlight}
      />
      <ReactivateUserConfirmModal
        isOpen={actionModal === 'reactivate'}
        onClose={closeActionModal}
        onConfirm={handleConfirmAction}
        userName={worker?.full_name || worker?.email}
        isReactivating={actionInFlight}
      />
    </div>
  );
};

export default WorkerDetail;